/** Share an in-flight read and cache only a successful settled result. */
export function canonicalRequestKey(key: string): string {
  if (!/^https?:\/\//.test(key)) return key;
  const url = new URL(key);
  url.searchParams.sort();
  return `${url.origin}${url.pathname}${url.search}`;
}

export function createCoalescer({ now }: { now: () => number }) {
  const flights = new Map<string, Promise<unknown>>();
  const cache = new Map<string, { value: unknown; receivedAtMs: number; expires: number }>();
  return {
    async get<T>(key: string, run: () => Promise<T>, ttlMs: number): Promise<{ value: T; cacheAgeMs: number }> {
      if (!Number.isFinite(ttlMs) || ttlMs < 0) throw new RangeError("coalescer: invalid ttlMs");
      key = canonicalRequestKey(key);
      const flight = flights.get(key);
      if (flight) return { value: structuredClone(await flight) as T, cacheAgeMs: 0 };
      const hit = cache.get(key);
      const readAtMs = now();
      if (ttlMs > 0 && hit && readAtMs < hit.expires) {
        return { value: structuredClone(hit.value) as T, cacheAgeMs: Math.max(0, readAtMs - hit.receivedAtMs) };
      }
      cache.delete(key);
      const startedAtMs = readAtMs;
      const request = Promise.resolve().then(run);
      flights.set(key, request);
      try {
        const value = await request;
        const receivedAtMs = now();
        if (ttlMs > 0) cache.set(key, { value, receivedAtMs, expires: startedAtMs + ttlMs - 250 });
        return { value: structuredClone(value), cacheAgeMs: 0 };
      } finally {
        flights.delete(key);
      }
    },
  };
}

/** Advance only ages the server actually supplied; never mutate the cached original. As edgeCache.ts:236-245 ages a
 * stored copy: every top-level finite age_s and *_age_s (last_price_age_s feeds the tick chip) except the edge's own
 * cache_age_s, and stale re-derives from age_s against budget_s without ever clearing a served true. */
export function ageServedValue<T>(value: T, cacheAgeMs: number): T {
  if (cacheAgeMs <= 0 || value === null || typeof value !== "object" || Array.isArray(value)) return value;
  const served = value as Record<string, unknown>;
  const aged: Record<string, unknown> = { ...served };
  for (const [name, age] of Object.entries(served)) {
    if ((name === "age_s" || name.endsWith("_age_s")) && name !== "cache_age_s" && typeof age === "number" && Number.isFinite(age)) {
      aged[name] = age + cacheAgeMs / 1_000;
    }
  }
  if (typeof aged.age_s === "number" && typeof served.budget_s === "number" && typeof served.stale === "boolean") {
    aged.stale = served.stale || aged.age_s > served.budget_s;
  }
  return aged as T;
}

export const sharedCoalescer = createCoalescer({ now: () => Date.now() });
